import React from 'react';

import {type ArtifactVersionsTableData} from '../ArtifactOverview/hooks';
import {getInitialArtifactVersionMembershipIds} from './versionSelectionUtils';

type UseInitialArtifactVersionMembershipSelectionArgs = {
  artifactCollectionId?: string;
  debouncedSearch: string;
  loading: boolean;
  membershipIds: string[];
  page: number;
  pageRows: ArtifactVersionsTableData[];
  readOnly: boolean;
  search: string;
  updateMembershipIds: (membershipIds: string[]) => void;
};

type InitialArtifactVersionMembershipSelectionStatusArgs = {
  artifactCollectionId?: string;
  attemptedSelectionCollectionId: string | null;
  loading: boolean;
  membershipIdCount: number;
  page: number;
  pageRowCount: number;
  readOnly: boolean;
  searchIsActive: boolean;
};

export function getIsWaitingForInitialArtifactVersionMembershipSelection({
  artifactCollectionId,
  attemptedSelectionCollectionId,
  loading,
  membershipIdCount,
  page,
  pageRowCount,
  readOnly,
  searchIsActive,
}: InitialArtifactVersionMembershipSelectionStatusArgs) {
  return (
    !readOnly &&
    artifactCollectionId != null &&
    artifactCollectionId !== '' &&
    attemptedSelectionCollectionId !== artifactCollectionId &&
    (loading || pageRowCount > 0) &&
    membershipIdCount === 0 &&
    page === 0 &&
    !searchIsActive
  );
}

export function useInitialArtifactVersionMembershipSelection({
  artifactCollectionId,
  debouncedSearch,
  loading,
  membershipIds,
  page,
  pageRows,
  readOnly,
  search,
  updateMembershipIds,
}: UseInitialArtifactVersionMembershipSelectionArgs) {
  // Tracks the collection we've already attempted initial selection for. Keyed
  // by collection id (not a plain boolean) so switching collections in a
  // mounted grid re-attempts the auto-selection instead of latching forever.
  const attemptedSelectionCollectionIdRef = React.useRef<string | null>(null);
  const searchIsActive = search.trim() !== '' || debouncedSearch.trim() !== '';
  const waitingForInitialSelection =
    getIsWaitingForInitialArtifactVersionMembershipSelection({
      artifactCollectionId,
      // oxlint-disable-next-line react/refs
      attemptedSelectionCollectionId: attemptedSelectionCollectionIdRef.current,
      loading,
      membershipIdCount: membershipIds.length,
      page,
      pageRowCount: pageRows.length,
      readOnly,
      searchIsActive,
    });

  React.useEffect(() => {
    if (
      readOnly ||
      !artifactCollectionId ||
      attemptedSelectionCollectionIdRef.current === artifactCollectionId
    ) {
      return;
    }

    if (membershipIds.length > 0 || page !== 0 || searchIsActive) {
      attemptedSelectionCollectionIdRef.current = artifactCollectionId;
      return;
    }

    if (loading) {
      return;
    }

    attemptedSelectionCollectionIdRef.current = artifactCollectionId;
    const initialMembershipIds =
      getInitialArtifactVersionMembershipIds(pageRows);
    if (initialMembershipIds.length > 0) {
      updateMembershipIds(initialMembershipIds);
    }
  }, [
    artifactCollectionId,
    loading,
    membershipIds.length,
    page,
    pageRows,
    readOnly,
    searchIsActive,
    updateMembershipIds,
  ]);

  return {waitingForInitialSelection};
}
